import { FieldError } from '@HoudiniUI/Field';
import { IRegEdSubmissionMetadata } from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { Button } from '@HoudiniUI/Button';
import { css } from 'aphrodite';
import * as React from 'react';
import { CampaignType } from '../../../../../../models/AdminModels';
import { RegEdComplianceApprovalPrompt } from '../../../../../components/campaigns/ComplianceApprovalPrompt/RegEdComplianceApprovalPrompt';
import { bs } from '../../../../../styles/styles';
import { MobileImageUploadButtons } from './MobileImageUploadButtons';
import { mobileStyles } from '../styles';

const MAX_MOBILE_IMAGES = 5;

interface MobileActionButtonsProps {
	onScheduleForLater: () => void;
	onPostNow: () => void;
	onUploadMedia?: () => void;
	onFreeImages?: () => void;
	canPostNow: boolean;
	canSchedule: boolean;
	isSchedulingInProgress: boolean;
	showPostNow: boolean;
	imageCount?: number;
	usesRegEd?: boolean;
	/** Officer files from the review portal, so the advisor fills no questionnaire here. */
	officerFilesRegEd?: boolean;
	officerReviewerMissing?: boolean;
	onScheduleRegEdCompliance?: (e: React.MouseEvent<HTMLElement>, metadata: IRegEdSubmissionMetadata) => void;
	onScheduleWithoutCompliance?: (e: React.MouseEvent<HTMLElement>) => void;
	regEdMetadata?: IRegEdSubmissionMetadata | null;
	regEdRevisesApprovalRequestId?: string | null;
	regEdOwnerUserId?: string | null;
	socialProviders?: readonly Api.SocialMediaType[];
	impersonationCtx?: Api.IImpersonationContext;
}

const MobileActionButtons: React.FC<MobileActionButtonsProps> = ({
	onScheduleForLater,
	onPostNow,
	onUploadMedia,
	onFreeImages,
	canPostNow,
	canSchedule,
	isSchedulingInProgress,
	showPostNow,
	imageCount = 0,
	usesRegEd,
	officerFilesRegEd,
	officerReviewerMissing,
	onScheduleRegEdCompliance,
	onScheduleWithoutCompliance,
	regEdMetadata,
	regEdRevisesApprovalRequestId,
	regEdOwnerUserId,
	socialProviders,
	impersonationCtx,
}) => {
	const shouldShowImageButtons =
		!!(onUploadMedia || onFreeImages) && imageCount < MAX_MOBILE_IMAGES && !isSchedulingInProgress;
	const showRegEdPrompt = usesRegEd && !officerFilesRegEd && !!onScheduleRegEdCompliance;

	return (
		<div
			className={css(mobileStyles.actionButtonsContainer, showRegEdPrompt && mobileStyles.actionButtonsContainerRegEd)}
			data-reged-host={showRegEdPrompt ? '' : undefined}
		>
			{shouldShowImageButtons && (
				<MobileImageUploadButtons
					onClickFreeImage={onFreeImages}
					onClickMedia={onUploadMedia}
					currentImageCount={imageCount}
					maxImages={MAX_MOBILE_IMAGES}
				/>
			)}

			<div className={css(mobileStyles.actionButtons, showRegEdPrompt && mobileStyles.actionButtonsRegEd)}>
				{showRegEdPrompt ? (
					<RegEdComplianceApprovalPrompt
						impersonationContext={impersonationCtx}
						campaignType={CampaignType.Social}
						socialProviders={socialProviders}
						collapsible
						disabled={!canSchedule}
						isLoading={isSchedulingInProgress}
						initialFormat={regEdMetadata?.format}
						initialAnswers={regEdMetadata?.answers}
						revisesApprovalRequestId={regEdRevisesApprovalRequestId}
						ownerUserId={regEdOwnerUserId}
						onScheduleRegEdCompliance={onScheduleRegEdCompliance}
						onScheduleWithoutCompliance={onScheduleWithoutCompliance}
						styles={[mobileStyles.regEdPromptInSidebar]}
					/>
				) : (
					<>
						{officerReviewerMissing ? (
							<FieldError>
								No compliance email is set for this account, so nobody would be asked to review this post. Ask your
								administrator to add one before scheduling.
							</FieldError>
						) : null}
						<Button
							styles={[mobileStyles.scheduleButton]}
							onClick={onScheduleForLater}
							disabled={!canSchedule || isSchedulingInProgress || !!officerReviewerMissing}
						>
							{isSchedulingInProgress ? 'Scheduling...' : 'Schedule for Later'}
						</Button>
					</>
				)}

				{showPostNow && (
					<button
						className={css(bs.ctaButtonReverse)}
						onClick={onPostNow}
						disabled={!canPostNow || isSchedulingInProgress}
					>
						Post Now
					</button>
				)}
			</div>
		</div>
	);
};

export { MobileActionButtons };
